/*
 * Authors: mariushirjanu, Koen
 * Created: 20/04/2015
 * Upgraded: 27/12/2022
 */
import { Component, ViewEncapsulation, OnInit, Input, OnDestroy } from '@angular/core';
import { StateService } from '@uirouter/angular';
import { IObject } from '../../../../../type_definitions/datatypes';
import { BuyingGuideModel } from '../../catalog/guide/buying-guide.model';
import { OrderCatalogService } from '../../catalog/order-catalog.service';
import { OrderCatalogDataService } from '../../catalog/order-catalog-data.service';
import { STATE_ORDERENTRY_CATALOG_GUIDE, STATE_ORDERENTRY_CATALOG_OVERVIEW } from '../../../../shared/app-states';
import { DEFAULT_STOCK_ORDER_TYPE, OrderEntryInfoService } from '../order-info/order-entry-info.service';
import { CatalogStateService } from '../../catalog/catalog-state.service';
import { Subscription } from 'rxjs';
import { GaEventService, IGAEvent } from '../../../../shared/services/ga-event.service';
import { EVENT_BREADCRUMBS_CHANGED } from '../../../../shared/app-events';
import { IBreadcrumb } from '../../../../shared/breadcrumbs/breadcrumb.service';

@Component({
  selector: 'order-entry-catalog',
  template: `
    <div class="catalog">
      <h2 translate *ngIf="!buyingGuideId && !isOnlyExternal" class="catalog-title">CATALOG.TITLE</h2>

      <propose-alternatives *ngIf="stateName === '${STATE_ORDERENTRY_CATALOG_OVERVIEW}' && showProposeAlternatives && !loadingCategories && !isPunchoutCatalog && !isOnlyExternal && !isLoading"></propose-alternatives>

      <div class="row" *ngIf="isGuideVisible()">
        <div class="col-md-6">
          <button class="btn btn-primary guide-button" (click)="gotoGuide()">
            Buyer's guide
          </button>
        </div>

        <div class="col-md-6">
          <button class="btn btn-primary guide-button" (click)="showAll()" [disabled]="isShowAllDisabled()">
            Show complete catalog
          </button>
        </div>
      </div>
      <breadcrumbs [breadcrumbs]="breadcrumbs"></breadcrumbs>
      <ui-view></ui-view>
    </div>
  `,
  styleUrls: ['../../catalog/order-catalog.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OrderEntryCatalogComponent implements OnInit, OnDestroy {
  @Input() public orderId:number;
  @Input() public stateName:string;
  @Input() public stockOrderType:string;
  @Input() public punchoutMessageId:string;

  public buyingGuideId:number;
  public isPunchoutCatalog:boolean;
  public loadingCategories:boolean;
  public showProposeAlternatives:boolean;
  public isLoading:boolean;
  public breadcrumbs:IBreadcrumb[];

  private shopStateParams:IObject;
  private selectedStockOrderType:string;
  private eventHandler:Subscription;

  constructor(
    private buyingGuideModel:BuyingGuideModel,
    private eventService:GaEventService,
    private orderCatalogDataService:OrderCatalogDataService,
    private orderCatalogService:OrderCatalogService,
    private catalogStateService:CatalogStateService,
    private orderEntryInfoService:OrderEntryInfoService,
    private stateService:StateService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.stateName = this.stateService.current.name;
    this.buyingGuideId = this.catalogStateService.buyingGuideId;
    this.isPunchoutCatalog = this.orderCatalogService.punchoutCatalog;
    this.showProposeAlternatives = this.orderCatalogService.showProposeAlternatives;
    this.selectedStockOrderType = this.stockOrderType || <string>this.orderEntryInfoService.stockOrderType?.id || DEFAULT_STOCK_ORDER_TYPE;

    this.activate();
    this.listen();
  }

  private activate():void {
    this.orderCatalogService.isStarted()
      .then(() => {
        this.shopStateParams = this.createShopStateParams(this.orderCatalogService.billType, this.orderCatalogService.countryId, this.orderCatalogService.currency, this.orderCatalogService.showStock, this.orderCatalogService.webshopId);
        this.catalogStateService.baseParams = this.shopStateParams;
        this.getPossibleCategories();
      });
  }

  private listen():void {
    this.eventHandler = this.eventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_BREADCRUMBS_CHANGED) {
        this.breadcrumbs = event.data;
      }
    });
  }

  private getPossibleCategories():void {
    this.loadingCategories = true;
    this.orderCatalogDataService.categories(this.orderCatalogService.workflowType || 'PO', this.selectedStockOrderType)
      .finally(() => {
        this.loadingCategories = false;
      });
  }

  private createShopStateParams(billType:string, countryId:number, currency:string, stock:boolean, webshopId:number):IObject {
    return {
      billType: billType,
      country: countryId,
      currency: currency,
      stock: stock,
      webshop: webshopId,
      punchoutMessageId: this.punchoutMessageId,
      order: this.orderId
    };
  }

  public isGuideVisible():boolean {
    return this.catalogStateService.buyingGuideId && !this.stateService.is(STATE_ORDERENTRY_CATALOG_GUIDE);
  }

  public isShowAllDisabled():boolean {
    return this.buyingGuideModel.showingAll();
  }

  public gotoGuide():void {
    this.stateService.go(STATE_ORDERENTRY_CATALOG_GUIDE);
  }

  public showAll():void {
    this.buyingGuideModel.showingAll(true);
    this.stateService.go(STATE_ORDERENTRY_CATALOG_OVERVIEW, {
      guide: undefined,
      compat: undefined,
      terms: undefined,
      layout: 'block'
    });
  }

  public get isOnlyExternal():boolean {
    return this.orderCatalogService.isOnlyExternal;
  }

  public ngOnDestroy():void {
    this.eventHandler?.unsubscribe();
  }
}